import { connect } from 'react-redux';
import { goBack, push } from 'redux-first-history';
import parsePhoneNumber, { formatNumber } from 'libphonenumber-js';

import PhoneVerificationPage from './component';
import {
  getIreneDashboard,
  getLandlordStatus,
  requestPhoneVerificationConfirm,
  requestPhoneVerificationSend,
  requestUpdateListingPhoneNumber,
} from '@rental-manager/actions/requests';
import { editAccountInfoPage } from '@rental-manager/actions/OnboardingActions';
import { closeNotification, showModalNotification } from '@common/actions/NotificationActions';
import { checkRequestActionSuccess } from '@common/paymentsUtils/api';
import { trackValidationError } from '@common/actions/TrackingActions';
import {
  ID_VERIFICATION_UPDATED,
  PHONE_VERIFICATION_SEND_ERROR,
} from '@rental-manager/constants/NotificationConstants';
import HTTP_STATUS from '@common/constants/HttpConstants';
import API_MESSAGES from '@rental-manager/constants/ApiMessageConstants';
import paths from '@rental-manager/pathsManifest';
import { isHighRiskOTP } from '@common/paymentsUtils/moveInCostsUtil';
import { LANDLORD_STATUS } from '@rental-manager/constants/OnboardingConstants';
import { cleanPhoneNumber } from '@common/utils/numbersUtils';
import { logError } from '@zg-rentals/log-error';

const mapStateToProps = (state, { propertyId, fromDashboard }) => {
  const moveInCostsSettings = state.payments.landlord.onboarding[propertyId].moveInCostsSettings;
  const isHighRiskOTPPresent = isHighRiskOTP(moveInCostsSettings);

  const getPhoneNumber = () => {
    // Check for phone number in onboarding flow, inputted on tax verification page
    const phoneNumberFromOnboarding =
      state.payments.landlord.onboarding[propertyId]?.identityVerificationInfo?.phoneNumber;

    // Check for contact phone from listing details
    const contactPhoneFromListing = state.payments.landlord.property.listingDetails.contactPhone;

    // Check for phone number previously stored and in Stripe already
    const phoneNumberFromStripe = state.payments.landlord.property.phoneNumber;

    // Determine the final phone number based on precedence
    const phoneNumberToValidate = phoneNumberFromOnboarding || contactPhoneFromListing || phoneNumberFromStripe;
    let formattedPhoneNumber = '';
    const nationalNumber = parsePhoneNumber(phoneNumberToValidate, { defaultCountry: 'US' })?.nationalNumber;

    if (nationalNumber) {
      formattedPhoneNumber = formatNumber(
        {
          country: 'US',
          phone: nationalNumber,
        },
        'NATIONAL',
      );
    }

    return formattedPhoneNumber;
  };

  return {
    editMode: state.payments.landlord.onboarding[propertyId].identityVerificationInfo.editMode,
    phoneNumber: getPhoneNumber(),
    isHighRiskOTPPresent,
    fromDashboard,
    currentProperty: state.payments.landlord.property.listingDetails,
    user: state.user,
  };
};

const mapDispatchToProps = (dispatch, { baseUrl, propertyId, landlordStatus, fromDashboard, location }) => ({
  sendVerificationCode: ({ phoneNumber, verificationMethod }) =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        dispatch(
          requestPhoneVerificationSend({
            propertyId,
            phoneNumber: cleanPhoneNumber(phoneNumber),
            verificationMethod,
          }),
        );
      })
      .then(checkRequestActionSuccess)
      .catch((err = {}) => {
        logError({ error: err });
        dispatch(showModalNotification(PHONE_VERIFICATION_SEND_ERROR));
        return { error: true };
      }),
  confirmVerificationCode: ({ phoneNumber, verificationCode }, editMode, isHighRiskOTPPresent) => {
    const cleanedPhoneNumber = cleanPhoneNumber(phoneNumber);
    return Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() =>
        dispatch(
          requestPhoneVerificationConfirm({
            propertyId,
            phoneNumber: cleanedPhoneNumber,
            verificationCode,
          }),
        ),
      )
      .then(checkRequestActionSuccess)
      .then(() => dispatch(requestUpdateListingPhoneNumber({ propertyId, phone: cleanedPhoneNumber })))
      .then(checkRequestActionSuccess)
      .then(() => dispatch(getLandlordStatus({ propertyId })))
      .then(checkRequestActionSuccess)
      .then((action) => {
        if (fromDashboard) {
          return dispatch(
            push({
              pathname: baseUrl + paths.dashboard.editAccountInfoPage,
              state: {
                fromWhere: paths.dashboard.phoneVerificationPage,
              },
            }),
          );
        }

        if (location?.state?.fromWhere === paths.onboarding.confirmationPage) {
          dispatch(editAccountInfoPage({ propertyId }));
          return dispatch(
            push({
              pathname: baseUrl + paths.onboarding.accountInfoPage,
              state: { fromWhere: 'onboardingReview' },
            }),
          );
        }

        if (landlordStatus === LANDLORD_STATUS.NOT_STARTED || landlordStatus === LANDLORD_STATUS.ONBOARDING_STARTED) {
          return dispatch(
            push({
              pathname: baseUrl + paths.onboarding.accountInfoPage,
              state: {
                fromWhere: paths.onboarding.phoneVerificationPage,
              },
            }),
          );
        }

        const isHighRiskOtpApproved = action.payload?.data?.isHighRiskOtpApproved;
        if (isHighRiskOTPPresent && !isHighRiskOtpApproved) {
          return dispatch(push(baseUrl + paths.onboarding.knowledgeBasedAuthenticationPage));
        }
        return dispatch(
          push({
            pathname: baseUrl + paths.onboarding.confirmationPage,
            state: {
              fromWhere: paths.onboarding.phoneVerificationPage,
            },
          }),
        );
      })
      .then(() => editMode && dispatch(showModalNotification(ID_VERIFICATION_UPDATED)))
      .catch(({ action = {} }) => {
        if (action.payload && action.payload.status === HTTP_STATUS.SC_FORBIDDEN) {
          if (action.payload.response.error.message === API_MESSAGES.TOO_MANY_ATTEMPTS_ERROR) {
            return { error: true, tooManyAttempts: true };
          }
          return { error: true };
        }
        return Promise.reject({ action });
      })
      .catch((err = {}) => {
        logError({ error: err });
        return { error: true };
      });
  },
  onBack: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => dispatch(goBack())),
  onClose: () =>
    Promise.resolve()
      .then(() => dispatch(closeNotification()))
      .then(() => {
        if (fromDashboard) {
          return Promise.resolve()
            .then(() => dispatch(push(baseUrl)))
            .then(() => dispatch(getIreneDashboard({ propertyId })));
        } else {
          dispatch(push(baseUrl + paths.onboarding.exitPaymentSetup));
        }
      }),
  trackValidationError: (data) => dispatch(trackValidationError(data)),
});

const mergeProps = (stateProps, dispatchProps) => ({
  ...stateProps,
  ...dispatchProps,
  confirmVerificationCode: (...args) =>
    dispatchProps.confirmVerificationCode(...args, stateProps.editMode, stateProps.isHighRiskOTPPresent),
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(PhoneVerificationPage);
